/**
 * Module dependencies.
 */

import { FormGroup } from 'src/components/core/forms/styles';
import { HTMLProps, ReactNode, forwardRef } from 'react';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import checkSolidSvg from 'src/assets/svgs/checked-solid.svg';
import styled from 'styled-components';

/**
 * Export `RadioProps` type.
 */

export type RadioProps = Omit<HTMLProps<HTMLInputElement>, 'type' | 'as' | 'label'> & {
  className?: string;
  disabled?: boolean;
  error?: string;
  label: ReactNode;
};

/**
 * `RadioWrapper` styled component.
 */

const RadioWrapper = styled.span`
  border: 2px solid var(--radio-outer-border-color);
  border-radius: 50%;
  grid-area: radio;
  height: var(--radio-size);
  position: relative;
  top: 3px;
  transition: border-color var(--transition-fast);
  width: var(--radio-size);
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg)`
  color: var(--color-cyan400);
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: opacity var(--transition-fast);
`;

/**
 * `Label` styled component.
 */

const Label = styled.label<{
  disabled?: boolean;
  hasError?: boolean;
  hasLabel?: boolean;
}>`
  --radio-icon-color: var(--color-slate500);
  --radio-outer-border-color: var(--color-slate500);
  --radio-size: 18px;

  align-items: flex-start;
  color: var(--radio-label-color);
  cursor: pointer;
  display: grid;
  grid-column-gap: var(--space-xxs);
  grid-template-areas: 'radio label';
  grid-template-columns: var(--radio-size) 1fr;
  position: relative;
  width: 100%;
  width: max-content;

  ${Icon} {
    opacity: 0;
  }

  input:checked ~ ${RadioWrapper} {
    --radio-inner-border-color: transparent;

    ${Icon} {
      opacity: 1;
    }
  }

  :focus,
  :focus-visible,
  :hover {
    --radio-outer-border-color: var(--color-cyan400);
  }

  :disabled {
    --radio-outer-border-color: var(--color-slate400);
    --radio-icon-color: var(--color-slate500);

    color: var(--color-slate500);
    pointer-events: none;
  }

  &[data-error='true'] {
    --radio-outer-border-color: var(--color-error) !important;
  }
`;

/**
 * `Input` styled component.
 */

const Input = styled.input`
  cursor: pointer;
  grid-area: radio;
  height: var(--radio-size);
  opacity: 0;
  width: var(--radio-size);
  z-index: 1;
`;

/**
 * `LabelText` styled component.
 */

const LabelText = styled(Text)`
  align-self: center;
  color: var(--color-text);
  grid-area: label;
  transition: var(--transition-default);
  transition-property: color, opacity;
  word-break: break-word;
`;

/**
 * Export `InputRadio` component.
 */

export const InputRadio = forwardRef(
  ({ className, disabled, error, id, label, name, ...props }: RadioProps, ref: any) => {
    return (
      <FormGroup className={className} data-cursor={'none'} style={{ marginBottom: '0px' }}>
        <Label data-error={!!error} disabled={disabled}>
          <Input disabled={disabled} id={id ?? name} name={name} ref={ref} type={'radio'} {...props} />

          <LabelText>{label}</LabelText>

          <RadioWrapper>
            <Icon icon={checkSolidSvg} size={'18px'} />
          </RadioWrapper>
        </Label>
      </FormGroup>
    );
  }
);

/**
 * `InputRadio` display name.
 */

InputRadio.displayName = 'InputRadio';
